import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";
import Icon from "../../Icon";

const BannerAlert = () => {
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  const toggleDismiss = (id) => {
    setDismissedAlerts(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* SECTION 1: ALERT TYPES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Alert Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Variation 1: Error Banner */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Error Banner</label>
            <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0"><Icon name="close" className="w-6 h-6" /></span>
              <div>
                <h4 className="font-semibold text-red-300">Payment Failed</h4>
                <p className="text-sm text-red-200 mt-1">Your transaction could not be processed. Please check your payment details.</p>
              </div>
            </div>
            <CopyCode number_code={51} code={`<div className="p-4 bg-red-900/20 border border-red-700/50 
  rounded-lg flex items-start gap-3">
  <span className="text-2xl">Icon</span>
  <div>
    <h4 className="font-semibold text-red-300">Payment Failed</h4>
    <p className="text-sm text-red-200 mt-1">
      Your transaction could not be processed.
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 2: Success Banner */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Success Banner</label>
            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0"><Icon name="check" className="w-6 h-6" /></span>
              <div>
                <h4 className="font-semibold text-green-300">Order Confirmed</h4>
                <p className="text-sm text-green-200 mt-1">Your order has been successfully placed. Tracking number: #ORD123456</p>
              </div>
            </div>
            <CopyCode number_code={52} code={`<div className="p-4 bg-green-900/20 border border-green-700/50 
  rounded-lg flex items-start gap-3">
  <span className="text-2xl">Icon</span>
  <div>
    <h4 className="font-semibold text-green-300">Order Confirmed</h4>
    <p className="text-sm text-green-200 mt-1">
      Your order has been placed successfully.
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 3: Warning Banner */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Warning Banner</label>
            <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0"><Icon name="warning" className="w-6 h-6" /></span>
              <div>
                <h4 className="font-semibold text-amber-300">Low Disk Space</h4>
                <p className="text-sm text-amber-200 mt-1">Your device is running out of storage. Please free up some space.</p>
              </div>
            </div>
            <CopyCode number_code={53} code={`<div className="p-4 bg-amber-900/20 border border-amber-700/50 
  rounded-lg flex items-start gap-3">
  <span className="text-2xl">Icon</span>
  <div>
    <h4 className="font-semibold text-amber-300">Low Disk Space</h4>
    <p className="text-sm text-amber-200 mt-1">
      Please free up some storage space.
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 4: Info Banner */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Info Banner</label>
            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0"><Icon name="info" className="w-6 h-6" /></span>
              <div>
                <h4 className="font-semibold text-blue-300">New Feature Available</h4>
                <p className="text-sm text-blue-200 mt-1">We've added dark mode support. Check your settings to enable it.</p>
              </div>
            </div>
            <CopyCode number_code={54} code={`<div className="p-4 bg-blue-900/20 border border-blue-700/50 
  rounded-lg flex items-start gap-3">
  <span className="text-2xl">Icon</span>
  <div>
    <h4 className="font-semibold text-blue-300">New Feature Available</h4>
    <p className="text-sm text-blue-200 mt-1">
      Check your settings to enable it.
    </p>
  </div>
</div>`} />
          </div>

        </div>
      </div>

      {/* SECTION 2: DISMISSIBLE & INTERACTIVE ALERTS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Dismissible Alerts</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 5: Dismissible Alert */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Dismissible Alert</label>
            {!dismissedAlerts['alert1'] && (
              <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg flex items-start gap-3">
                <span className="text-2xl flex-shrink-0"><Icon name="arrowRight" className="w-6 h-6" /></span>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-300">New Update Available</h4>
                  <p className="text-sm text-amber-200 mt-1">Version 2.1.0 is ready to download.</p>
                </div>
                <button onClick={() => toggleDismiss('alert1')} className="text-slate-400 hover:text-slate-200 ml-2"><Icon name="close" className="w-4 h-4" /></button>
              </div>
            )}
            {dismissedAlerts['alert1'] && <p className="text-xs text-slate-400 p-2">Alert dismissed</p>}
            <CopyCode number_code={55} code={`const [dismissed, setDismissed] = useState(false);

{!dismissed && (
  <div className="p-4 bg-amber-900/20 border border-amber-700/50 
    rounded-lg flex items-start gap-3">
    <span className="text-2xl">Icon</span>
    <div className="flex-1">
      <h4 className="font-semibold text-amber-300">
        New Update Available
      </h4>
      <p className="text-sm text-amber-200 mt-1">
        Version 2.1.0 is ready.
      </p>
    </div>
    <button onClick={() => setDismissed(true)}>Close</button>
  </div>
)}`} />
          </div>

          {/* Variation 6: Alert with Action Button */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Action Button</label>
            <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0"><Icon name="gift" className="w-6 h-6" /></span>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-300">Claim Your Bonus</h4>
                  <p className="text-sm text-amber-200 mt-1">You have $50 in account credits waiting.</p>
                </div>
              </div>
              <button className="mt-3 px-3 py-1 bg-amber-600 hover:bg-amber-500 text-xs font-semibold text-white rounded w-full">Claim Now</button>
            </div>
            <CopyCode number_code={56} code={`<div className="p-4 bg-amber-900/20 border border-amber-700/50 
  rounded-lg">
  <div className="flex items-start gap-3">
    <span className="text-2xl">Icon</span>
    <div className="flex-1">
      <h4 className="font-semibold text-amber-300">
        Claim Your Bonus
      </h4>
      <p className="text-sm text-amber-200 mt-1">
        You have $50 credits waiting.
      </p>
    </div>
  </div>
  <button className="mt-3 w-full px-3 py-1 
    bg-amber-600 text-white rounded">
    Claim Now
  </button>
</div>`} />
          </div>

          {/* Variation 7: Stacked Alerts */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Stacked Alerts</label>
            <div className="space-y-2">
              <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-lg flex items-start gap-2">
                <span className="text-lg flex-shrink-0">1</span>
                <p className="text-sm text-orange-200">Email not verified</p>
              </div>
              <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-2">
                <span className="text-lg flex-shrink-0">2</span>
                <p className="text-sm text-red-200">Password expires soon</p>
              </div>
            </div>
            <CopyCode number_code={57} code={`<div className="space-y-2">
  <div className="p-3 bg-orange-900/20 
    border border-orange-700/50 rounded">
    <span className="text-lg">1</span>
    <p className="text-sm text-orange-200">
      Email not verified
    </p>
  </div>
  <div className="p-3 bg-red-900/20 
    border border-red-700/50 rounded">
    <span className="text-lg">2</span>
    <p className="text-sm text-red-200">
      Password expires soon
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 8: Animated Alert */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Live Activity</label>
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/50 rounded-lg flex items-start gap-3 animate-pulse">
              <span className="text-2xl flex-shrink-0 animate-bounce"><Icon name="dot" className="w-5 h-5" /></span>
              <div>
                <h4 className="font-semibold text-emerald-300">System Update In Progress</h4>
                <p className="text-sm text-emerald-200 mt-1">Estimated time: 2 minutes remaining</p>
              </div>
            </div>
            <CopyCode number_code={58} code={`<div className="p-4 bg-emerald-900/20 
  border border-emerald-700/50 rounded-lg 
  flex items-start gap-3 animate-pulse">
  <span className="text-2xl animate-bounce">Status</span>
  <div>
    <h4 className="font-semibold text-emerald-300">
      System Update In Progress
    </h4>
    <p className="text-sm text-emerald-200 mt-1">
      Estimated time: 2 minutes
    </p>
  </div>
</div>`} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default BannerAlert;


